import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Droplets, ArrowLeft, Copy, Check, MessageCircle,
  Gift, Trophy, Zap, Link as LinkIcon
} from 'lucide-react';

export default function Invite() {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [messageCopied, setMessageCopied] = useState(false);

  const referralCode = user?.referral_code || 'CARGANDO...';
  const validatedReferrals = user?.validated_referrals || 0;
  const GOAL = 10;
  const remaining = Math.max(0, GOAL - validatedReferrals);

  const appUrl = window.location.origin;
  const referralLink = `${appUrl}/ref/${referralCode}`;

  const whatsappMessage = `¡Ey! Estoy usando Manklar para tener agua sin garrafas y sin plástico 💧

Prueba gratis aquí:
${referralLink}

Si te instalas, ambos ganamos beneficios 😎`;

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(whatsappMessage)}`;

  const copyCode = () => {
    navigator.clipboard.writeText(referralCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const copyMessage = () => {
    navigator.clipboard.writeText(whatsappMessage);
    setMessageCopied(true);
    setTimeout(() => setMessageCopied(false), 2000);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#050505' }}>
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3 text-[#A1A1AA] hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Volver al Dashboard</span>
          </Link>
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-[#0A0A0A] border border-white/10 flex items-center justify-center">
              <Droplets className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-light text-white font-['Outfit']">Manklar</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-8 py-10 md:py-14">
        
        {/* Hero Section - IMPROVED */}
        <div className="text-center mb-10 animate-fade-in-up">
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-white font-['Outfit'] mb-3">
            📲 Comparte tu código y empieza a ganar meses gratis
          </h1>
          <p className="text-xl text-[#A1A1AA]">
            💸 Cada referido validado = <span className="text-white font-bold">1 mes gratis</span>
          </p>
        </div>

        {/* Progress reminder */}
        {remaining > 0 && (
          <div className="p-4 rounded-sm mb-8 bg-white/5 border border-white/10 text-center animate-fade-in-up">
            <p className="text-white">
              🔥 Te {remaining === 1 ? 'falta' : 'faltan'} <span className="font-bold text-xl">{remaining}</span> {remaining === 1 ? 'referido' : 'referidos'} para conseguir <span className="font-bold">6 meses GRATIS</span>
            </p>
          </div>
        )}

        {/* Primary WhatsApp CTA - VERY PROMINENT */}
        <div className="mb-8 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <a 
            href={whatsappUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="block"
          >
            <Button 
              className="w-full h-20 bg-[#25D366] text-black hover:bg-[#20bd5a] rounded-sm font-bold text-xl transition-all shadow-[0_0_30px_rgba(37,211,102,0.4)] hover:shadow-[0_0_40px_rgba(37,211,102,0.6)] flex items-center justify-center gap-4"
              data-testid="whatsapp-share-btn"
            >
              <MessageCircle className="w-8 h-8" />
              Invitar por WhatsApp 🚀
            </Button>
          </a>
          <p className="text-center text-[#52525B] text-sm mt-3">
            ⚡ Empieza hoy y consigue tu primer mes gratis en pocos días
          </p>
        </div>

        {/* Referral Link Card - NEW */}
        <div className="p-6 rounded-sm mb-6 bg-[#0A0A0A] border border-white/20 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <div className="flex items-center gap-2 mb-3">
            <LinkIcon className="w-4 h-4 text-[#25D366]" />
            <span className="text-xs tracking-[0.2em] uppercase text-[#52525B]">Tu enlace personalizado</span>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch gap-3">
            <div className="flex-1 p-3 rounded-sm bg-[#121212] border border-white/10 overflow-hidden">
              <p className="text-white font-mono text-sm truncate" data-testid="referral-link">
                {referralLink}
              </p>
            </div>
            <Button
              onClick={copyLink}
              className="h-auto px-6 bg-[#25D366] text-black hover:bg-[#20bd5a] rounded-sm font-medium"
              data-testid="copy-link-btn"
            >
              {linkCopied ? (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  ¡Copiado!
                </>
              ) : (
                <>
                  <Copy className="w-5 h-5 mr-2" />
                  Copiar Enlace
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-[#A1A1AA] mt-3">
            🎯 Cuando alguien abra este enlace, tu código se aplicará automáticamente
          </p>
        </div>

        {/* Referral Code Card */}
        <div className="p-6 rounded-sm mb-8 bg-[#0A0A0A] border border-white/10 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <span className="text-xs tracking-[0.2em] uppercase text-[#52525B]">Tu código de referido</span>
              <div className="mt-2">
                <span 
                  className="text-4xl font-mono text-white tracking-widest"
                  data-testid="invite-referral-code"
                >
                  {referralCode}
                </span>
              </div>
            </div>
            <Button
              onClick={copyCode}
              className="h-12 px-6 bg-white/10 text-white border border-white/20 hover:bg-white/20 rounded-sm"
              data-testid="copy-code-btn"
            >
              {copied ? (
                <>
                  <Check className="w-5 h-5 mr-2 text-[#25D366]" />
                  ¡Copiado!
                </>
              ) : (
                <>
                  <Copy className="w-5 h-5 mr-2" />
                  Copiar Código
                </>
              )}
            </Button>
          </div>
        </div>

        {/* WhatsApp Message Preview */}
        <div className="p-6 rounded-sm mb-8 bg-[#0A0A0A] border border-white/10 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          <h3 className="text-sm font-medium text-[#A1A1AA] mb-3 uppercase tracking-wider">
            Mensaje que se enviará:
          </h3>
          
          <div className="p-4 rounded-sm bg-[#121212] border border-white/5 mb-4">
            <p className="text-white whitespace-pre-line leading-relaxed">
              {whatsappMessage}
            </p>
          </div>

          <Button
            onClick={copyMessage}
            variant="ghost"
            className="text-[#A1A1AA] hover:text-white"
            data-testid="copy-message-btn"
          >
            {messageCopied ? (
              <>
                <Check className="w-4 h-4 mr-2 text-[#25D366]" />
                Mensaje copiado
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-2" />
                Copiar mensaje
              </>
            )}
          </Button>
        </div>

        {/* Rewards Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 animate-fade-in-up" style={{ animationDelay: '0.25s' }}>
          <div className="p-5 rounded-sm bg-[#0A0A0A] border border-white/10">
            <div className="flex items-center gap-3 mb-2">
              <Gift className="w-5 h-5 text-white" />
              <span className="text-sm text-[#A1A1AA]">Sistema 1x1</span>
            </div>
            <p className="text-white">
              <span className="text-2xl font-bold">1</span> referido validado = <span className="font-bold">1 mes gratis</span>
            </p>
          </div>
          <div className="p-5 rounded-sm bg-[#0A0A0A] border border-white/10">
            <div className="flex items-center gap-3 mb-2">
              <Trophy className="w-5 h-5 text-white" />
              <span className="text-sm text-[#A1A1AA]">Bonus Especial</span>
            </div>
            <p className="text-white">
              <span className="text-2xl font-bold">10</span> referidos = <span className="font-bold">6 meses GRATIS</span>
            </p>
          </div>
        </div>

        {/* How it Works - Simplified */}
        <div className="p-6 rounded-sm bg-[#0A0A0A] border border-white/10 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
          <h3 className="text-lg font-medium text-white mb-6 font-['Outfit']">
            🎯 ¿Cómo funciona?
          </h3>
          
          <div className="space-y-5">
            <div className="flex gap-4 items-start">
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm font-bold">1</span>
              </div>
              <div>
                <h4 className="text-white font-medium">Comparte tu código por WhatsApp</h4>
                <p className="text-sm text-[#A1A1AA]">
                  Envía el mensaje a amigos y familiares
                </p>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm font-bold">2</span>
              </div>
              <div>
                <h4 className="text-white font-medium">Ellos se suscriben con tu código</h4>
                <p className="text-sm text-[#A1A1AA]">
                  Aparecerán en tu lista de referidos
                </p>
              </div>
            </div>

            <div className="flex gap-4 items-start">
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm font-bold">3</span>
              </div>
              <div>
                <h4 className="text-white font-medium">3 meses pagados = referido validado</h4>
                <p className="text-sm text-[#A1A1AA]">
                  🎁 ¡Y tú ganas 1 mes gratis automáticamente!
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 rounded-sm bg-white/5 border border-white/10 flex items-center gap-4">
            <Zap className="w-6 h-6 text-white flex-shrink-0" />
            <div>
              <p className="text-white text-sm">
                <span className="font-bold">Sin límites:</span> Invita a cuantos amigos quieras y gana meses gratis ilimitados
              </p>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="mt-8 text-center animate-fade-in-up" style={{ animationDelay: '0.35s' }}>
          <a 
            href={whatsappUrl} 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Button 
              className="h-16 px-12 bg-[#25D366] text-black hover:bg-[#20bd5a] rounded-sm font-bold text-lg transition-all shadow-[0_0_25px_rgba(37,211,102,0.3)] hover:shadow-[0_0_35px_rgba(37,211,102,0.5)]"
              data-testid="whatsapp-share-btn-bottom"
            >
              <MessageCircle className="w-6 h-6 mr-3" />
              Empezar a Invitar 🚀
            </Button>
          </a>
        </div>
      </main>
    </div>
  );
}
